import { createSelector } from 'reselect';

import { emptyObject } from 'Lib/constants/placeholder';
import type { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';

import { STATE_LOADING } from 'Store/commonActions';
import { GENDER_MALE } from 'Store/entities/profiles.schema';
import { getProfileById, getProfilesByIds } from 'Store/entities/profiles.selectors';

import { QUEUE_MAIN } from './constants';

/**
 * Gets recs namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {RecsState} entities by type
 */

export const selectRecs = createKeySelector('recs');

export const getRecsState = createSelector(selectRecs, (state) => state.recs ?? emptyObject);

export const getSessionState = createSelector(selectRecs, (state) => state.session);

export const getCountsState = createSelector(selectRecs, (state) => state.counts);

export const getSuperlikeState = createSelector(selectRecs, (state) => state.superlike);

export const getMatchesState = createSelector(selectRecs, (state) => state.matches);

export const getRecsActiveQueue = createSelector(
  getRecsState,
  (recs) => recs.activeQueue || QUEUE_MAIN
);

export const getRecsIds = createSelector(
  [getRecsState, getRecsActiveQueue],
  (recs, activeQueue) => recs.queues?.[activeQueue]
);

export const getChatBubbleIntroState = createSelector(getRecsState, (recs) => recs.chatBubbleIntro);

/**
 * Get the id of swiped rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} swiped rec id
 */
export const getSwipedRecId = createSelector(getRecsState, (recs) => recs.previousRec);
/**
 * Get the swiped rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {UserProfile} swiped rec
 */
export const getSwipedRec = createSelector(
  (state: GlobalStateType) => state,
  getSwipedRecId,
  getProfileById
);

/**
 * Get the swiped rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<UserProfile>} swiped rec
 */
export const getRecsData = createSelector(
  (state: GlobalStateType) => state,
  getRecsIds,
  getProfilesByIds
);

/**
 * Do we need more recs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of recs
 */

export const getNumRecs = createSelector(getRecsIds, (queue) => queue?.length);

/**
 * Get id of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} id of current rec
 */
export const getCurrentRecId = createSelector(getRecsIds, (queue) => queue?.[0]);

/**
 * Get current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {User} current rec
 */
export const getCurrentRec = createSelector(
  (state: GlobalStateType) => state,
  getCurrentRecId,
  getProfileById
);

/**
 * Get first photo of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Photo} url string of first photo of current rec
 */
export const getFirstPhotoCurrentRec = createSelector(
  getCurrentRec,
  (currentRec) => currentRec?.photos?.[0] ?? emptyObject
);

/**
 * Get name of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} name of current rec
 */
export const getCurrentRecName = createSelector(
  getCurrentRec,
  (currentRec) => currentRec?.name ?? ''
);

/**
 * Get gender of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} gender of current rec
 */
export const getCurrentRecGender = createSelector(
  getCurrentRec,
  (currentRec) => currentRec?.gender ?? GENDER_MALE
);

/**
 * Get spotify anthem for current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ProfileSong} current rec anthem
 */

export const getCurrentRecAnthem = createSelector(
  getCurrentRec,
  (currentRec) => currentRec?.spotify?.spotify_theme_track ?? emptyObject
);

/**
 * Check if recs are loading
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} are recs loading
 */
export const getIsRecsLoading = createSelector(
  getRecsState,
  (recs) => recs.status === STATE_LOADING
);

/**
 * Get spotify top artists for current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<ProfileTopArtist>} current rec top artists
 */

export const getCurrentRecTopArtists = createSelector(getCurrentRec, (currentRec) => {
  const topArtists = currentRec?.spotify?.spotify_top_artists;
  return topArtists?.filter((artist) => !!artist.top_track?.preview_url);
});

/**
 * Gets the cache of previously swiped recs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array<string>} number of rec swipes
 */
export const getPreviouslySwiped = createSelector(getRecsState, (recs) =>
  recs.previouslySwiped?.map(({ id }) => id)
);

/**
 * Determine if out of recs
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} out of recs
 */
export const getIsOutOfRecs = createSelector(getSessionState, (session) => session.outOfRecs);

/**
 * Determine if out of likes
 * [TODO] we're currently have conflicting sources of truth for
 * this since we have these flags as well as likes/superlikes/boosts
 * counters that imply whether we're out of any of those things.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} is out of likes
 */
export const getRecsOutOfLikes = createSelector(getSessionState, (session) => session.outOfLikes);

/**
 * Determine if out of superlikes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} is out of superlikes
 */
export const getRecsOutOfSuperLikes = createSelector(
  getSessionState,
  (session) => session.outOfSuperlikes
);

/**
 * Determine the number of rec swipes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of rec swipes
 */
export const getRecSwipeCount = createSelector(getCountsState, (counts) => counts.swipeCount ?? 0);

/**
 * Determine the number of rec swipes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of rec swipes
 */
export const getPersistedSwipeCount = createSelector(
  getCountsState,
  (counts) => counts.persistedSwipeCount ?? 0
);

/**
 * Determine the number of right swipes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of right swipes
 */
export const getRightSwipeCount = createSelector(
  getCountsState,
  (counts) => counts.rightSwipeCount ?? 0
);

/**
 * Determine the number of sl swipes
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number of sl swipes
 */
export const getSuperlikeSwipeCount = createSelector(
  getCountsState,
  (counts) => counts.superlikeSwipeCount ?? 0
);

/**
 * Gets the liked photo array
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Array} liked photo array
 */
export const getLikedRecPhotos = createSelector(getRecsState, (recs) => recs.likedRecPhotos);

/**
 * Gets if should show superlike star animation
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if should show superlike star animation
 */
export const getShowSuperlikeStarAnimation = createSelector(
  getSuperlikeState,
  (superlike) => superlike.showStarAnimation
);

/**
 * Get gender of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} superlike upsell rec id
 */
export const getSuperlikeUpsellRecId = createSelector(
  getSuperlikeState,
  (superlike) => superlike.upsellRecId
);

/**
 * Get gender of current rec
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} gender of current rec
 */
export const getCurrentRecIsSuperlikeUpsellRec = createSelector(
  [getCurrentRecId, getSuperlikeUpsellRecId],
  (currRecId, superlikedRecId) => currRecId === superlikedRecId
);

export const getRecentlyMatchedMatchIds = createSelector(
  getMatchesState,
  (matches) => matches?.recentlyMatchedMatchIds
);

export const getIsRecsError = createSelector(getRecsState, (recs) => recs.error);
